import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, Edit2, Trash2, X, RefreshCw, ArrowRight, Database } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getEdiPartners, getEdiMappings, createEdiMapping, updateEdiMapping, deleteEdiMapping } from '../../config/api';

const TX_SETS = ['844', '845', '849'];

// Common internal target fields, offered as suggestions for the 844 inbound map.
const TARGET_FIELDS = [
  'contractNumber', 'customerId', 'contractAlias', 'claimNumber', 'claimDate',
  'invoiceNumber', 'invoiceDate', 'salesDate', 'ndc', 'quantity', 'unitPrice',
  'chargebackAmount', 'netAmount', 'customerName', 'customerDea', 'uom'
];

const emptyForm = { partnerId: '', transactionSet: '844', sourceField: '', targetField: '', transform: '', active: true };

const DataMappings = () => {
  const { hasRole } = useAuth();
  const canEdit = hasRole(['ADMIN']);
  const [mappings, setMappings] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filterTx, setFilterTx] = useState('');
  const [filterPartner, setFilterPartner] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterTx) params.transactionSet = filterTx;
      if (filterPartner) params.partnerId = filterPartner;
      const [m, p] = await Promise.all([getEdiMappings(params), getEdiPartners()]);
      setMappings(m); setPartners(p);
    } catch (e) { toast.error('Failed to load data mappings'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterTx, filterPartner]);

  const openAdd = () => { setEditingId(null); setForm({ ...emptyForm, transactionSet: filterTx || '844', partnerId: filterPartner || '' }); setShowForm(true); };
  const openEdit = (m) => {
    setEditingId(m.id);
    setForm({ partnerId: m.partnerId || '', transactionSet: m.transactionSet || '844', sourceField: m.sourceField || '', targetField: m.targetField || '', transform: m.transform || '', active: !!m.active });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.sourceField.trim() || !form.targetField.trim()) return toast.error('Source and target fields are required');
    setSaving(true);
    const payload = { ...form, partnerId: form.partnerId || null };
    try {
      if (editingId) { await updateEdiMapping(editingId, payload); toast.success('Mapping updated'); }
      else { await createEdiMapping(payload); toast.success('Mapping added'); }
      setShowForm(false);
      await load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to save mapping'); }
    finally { setSaving(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete mapping "${m.sourceField} → ${m.targetField}"?`)) return;
    try { await deleteEdiMapping(m.id); toast.success('Mapping deleted'); await load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed to delete mapping'); }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Data Mappings</h3>
          <p className="text-sm text-gray-500">Map a trading partner's inbound EDI fields to internal fields. Partner-scoped maps override the shared default (no partner).</p>
        </div>
        <div className="flex items-center space-x-2">
          <select value={filterTx} onChange={e => setFilterTx(e.target.value)} className="border border-gray-300 rounded-md px-2 py-2 text-sm">
            <option value="">All transactions</option>
            {TX_SETS.map(t => <option key={t} value={t}>EDI {t}</option>)}
          </select>
          <select value={filterPartner} onChange={e => setFilterPartner(e.target.value)} className="border border-gray-300 rounded-md px-2 py-2 text-sm max-w-[160px]">
            <option value="">All partners</option>
            {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button onClick={load} title="Refresh" className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </button>
          {canEdit && (
            <button onClick={openAdd} title="Add Mapping" className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700">
              <Plus className="h-4 w-4 mr-2" /> Add Mapping
            </button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50"><tr>
            {['Tx', 'Partner', 'Source Field', '', 'Target Field', 'Transform', 'Status', ''].map((h, i) => (
              <th key={i} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
            ))}
          </tr></thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-6 text-center text-sm text-gray-500">Loading…</td></tr>
            ) : mappings.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">
                <Database className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                No data mappings yet.{canEdit ? ' Click "Add Mapping" to define one.' : ''}
              </td></tr>
            ) : mappings.map(m => (
              <tr key={m.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-500">{m.transactionSet}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{m.partner?.name || <span className="italic text-gray-400">Default</span>}</td>
                <td className="px-4 py-3 text-sm font-mono text-gray-900">{m.sourceField}</td>
                <td className="px-2 py-3 text-gray-300"><ArrowRight className="h-4 w-4" /></td>
                <td className="px-4 py-3 text-sm font-mono text-primary-700">{m.targetField}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{m.transform || '—'}</td>
                <td className="px-4 py-3 text-sm">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${m.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>{m.active ? 'Active' : 'Inactive'}</span>
                </td>
                <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                  {canEdit && (
                    <>
                      <button onClick={() => openEdit(m)} className="text-gray-500 hover:text-primary-700 mr-3" title="Edit"><Edit2 className="h-4 w-4" /></button>
                      <button onClick={() => remove(m)} className="text-gray-500 hover:text-red-600" title="Delete"><Trash2 className="h-4 w-4" /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setShowForm(false)} />
          <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">{editingId ? 'Edit' : 'Add'} Data Mapping</h3>
              <button onClick={() => setShowForm(false)} title="Close" className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={submit} className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Set</label>
                  <select value={form.transactionSet} onChange={e => setForm({ ...form, transactionSet: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                    {TX_SETS.map(t => <option key={t} value={t}>EDI {t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Partner</label>
                  <select value={form.partnerId} onChange={e => setForm({ ...form, partnerId: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm">
                    <option value="">Default (all partners)</option>
                    {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source Field *</label>
                <input value={form.sourceField} onChange={e => setForm({ ...form, sourceField: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono" placeholder="e.g. IT1-07 or column header" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Field *</label>
                <input list="target-fields" value={form.targetField} onChange={e => setForm({ ...form, targetField: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono" placeholder="internal field name" />
                <datalist id="target-fields">
                  {TARGET_FIELDS.map(t => <option key={t} value={t} />)}
                </datalist>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transform (optional)</label>
                <input value={form.transform} onChange={e => setForm({ ...form, transform: e.target.value })} className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono" placeholder="e.g. DATE:YYYYMMDD, UPPER, TRIM" />
              </div>
              <label className="inline-flex items-center text-sm text-gray-700">
                <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} className="mr-2 rounded" /> Active
              </label>
              <div className="flex justify-end space-x-3 pt-2 border-t border-gray-200">
                <button type="button" onClick={() => setShowForm(false)} title="Cancel" className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} title={editingId ? 'Save Changes' : 'Add Mapping'} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50">{saving ? 'Saving…' : (editingId ? 'Save Changes' : 'Add Mapping')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataMappings;
